import { useState, type FormEvent } from 'react';
import { changePassword } from '@/api/user';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { SectionHeader } from './SectionHeader';

interface ChangePasswordSectionProps {
  updateIsQuerying: (querying: boolean) => void;
}

export function ChangePasswordSection({ updateIsQuerying }: ChangePasswordSectionProps) {
  const { notify } = useNotification();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Form is not complete' });
      return;
    }
    if (newPassword !== confirmPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    setSuccess(false);
    updateIsQuerying(true);
    try {
      await changePassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
      notify({ id: String(Date.now()), type: 'success', message: 'Password changed' });
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not change password' });
    } finally {
      setLoading(false);
      updateIsQuerying(false);
    }
  }

  return (
    <div className="my-4 mb-12">
      <SectionHeader>Change password</SectionHeader>
      <form onSubmit={handleSubmit} className="max-w-[700px] space-y-4 pl-1 pt-2">
        <Input
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          placeholder="Old password"
          className={success ? 'border-primary' : ''}
        />
        <Input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="New password"
          className={success ? 'border-primary' : ''}
        />
        <Input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm password"
          className={success ? 'border-primary' : ''}
        />
        <div className="pt-2">
          <Button type="submit" disabled={loading}>
            {loading && <Spinner size="sm" />}
            Change
          </Button>
        </div>
      </form>
    </div>
  );
}
